import * as React from "react";
import ReactMarkdown from "react-markdown";
import { FileText } from "lucide-react";
import { useToggle } from "react-use";
import clsx from "clsx";
import * as analytics from "@/services/analytics";
import { SummaryHeader } from "./SummaryHeader";
import { summaryMarkdownComponents } from "./markdownComponents";

interface Props {
  name: string;
  summary: string;
  defaultCollapsed?: boolean;
}

const TRUNCATE_LENGTH = 500;

export const ChatEmptyState: React.FC<Props> = ({ name, summary, defaultCollapsed = false }) => {
  const [isCollapsed, toggleCollapsed] = useToggle(defaultCollapsed);
  const [isExpanded, toggleExpanded] = useToggle(false);
  const shouldTruncate = summary.length > TRUNCATE_LENGTH;
  const displaySummary = shouldTruncate && !isExpanded ? summary.slice(0, TRUNCATE_LENGTH) + "..." : summary;

  return (
    <div className="mx-auto mb-4 w-full max-w-[700px] rounded-lg border border-brand-gold-100 bg-white/70 p-4 duration-300 animate-in fade-in slide-in-from-top-2">
      <SummaryHeader title="Document Summary" className="mb-3" />
      <button
        onClick={
          defaultCollapsed
            ? () => {
                if (isCollapsed) analytics.showSummary(name);
                toggleCollapsed();
              }
            : undefined
        }
        className={clsx(
          "flex w-full items-center gap-2 text-sm font-medium",
          defaultCollapsed && "cursor-pointer hover:text-primary",
        )}
      >
        <FileText className="h-4 w-4" />
        <span className="flex-1 text-left">{name}</span>
        {defaultCollapsed && (
          <span className="text-xs text-muted-foreground">{isCollapsed ? "Show summary" : "Hide summary"}</span>
        )}
      </button>
      {!isCollapsed && (
        <>
          <div className="mt-3 grid grid-rows-[1fr] transition-all duration-300 ease-in-out">
            <div className="prose prose-sm max-w-none overflow-hidden text-foreground">
              <ReactMarkdown components={summaryMarkdownComponents}>{displaySummary}</ReactMarkdown>
            </div>
          </div>
          {shouldTruncate && (
            <button onClick={toggleExpanded} className="mt-2 text-sm text-primary hover:underline">
              {isExpanded ? "Show less" : "Show more"}
            </button>
          )}
        </>
      )}
    </div>
  );
};
